import { ReactComponent as icSymbol } from '@assets/components/panel/notification/symbolStatus.svg';
import { HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectLanguage } from '@redux/settings/settings.selectors';
import { useRenderMessageTemplateQuery } from '@services/chat/chat.api';
import { getAlertLevelColor } from '@utils/data/notification.utils';
import { formatDate } from '@utils/date.utils';
import { getSettingsLocale } from '@utils/locale.utils';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { MessageRequest, MessageRequestStatusEnum } from '@/types/chat/chat.types';
import { DateTimeEnum } from '@/types/dateTime.types';

const STATUS_COLOR: Record<MessageRequestStatusEnum, string> = {
  [MessageRequestStatusEnum.TO_SEND]: 'skyBlue',
  [MessageRequestStatusEnum.TO_RETRY]: 'orange.500',
  [MessageRequestStatusEnum.ERROR]: 'red.500',
  [MessageRequestStatusEnum.SENT]: 'green.500',
};

export default function Message({ message }: Readonly<{ message: MessageRequest }>) {
  const locale = getSettingsLocale(useAppSelector(selectLanguage));
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data, isLoading, isError } = useRenderMessageTemplateQuery({
    id: message.id,
    locale,
    zone,
  });

  return (
    <VStack width="100%" gap={2} alignItems="start" padding={5} backgroundColor="neutral.700">
      <VStack width="100%" gap={0} alignItems="start">
        <HStack width="100%" justifyContent="space-between" alignItems="start" gap={2}>
          <Text>
            <FormattedMessage id="notification.message.automatic" />
          </Text>
          <Text
            textStyle="md"
            fontWeight="medium"
            paddingY={0.25}
            paddingX={0.5}
            backgroundColor="neutral.black"
            color={STATUS_COLOR[message.status]}
          >
            <FormattedMessage id={`notification.message.status.${message.status}`} />
          </Text>
        </HStack>
        <Text color="neutral.300">
          <FormattedMessage
            id="notification.message.date"
            values={{
              date: formatDate(message.content.time, DateTimeEnum.DATE_FULL),
              time: format(new Date(message.content.time), "HH':'mm'"),
            }}
          />
        </Text>
      </VStack>
      <Text width="100%" padding={2} border="2px solid" borderColor="neutral.850" whiteSpace="pre-wrap">
        {isLoading ? (
          <Spinner size="sm" color="neutral.white" />
        ) : isError || !data ? (
          <FormattedMessage id="notification.message.renderError" />
        ) : (
          <>
            <Icon
              boxSize="14px"
              as={icSymbol}
              color={getAlertLevelColor(message.content.alertLevel)}
              marginRight={0.5}
              marginTop="-2px"
            />
            {data.text}
          </>
        )}
      </Text>
    </VStack>
  );
}
